import React, { useState, useRef, useCallback } from 'react';

interface AdvancedDateNavigatorProps {
  startDate: Date;
  endDate: Date;
  minDate?: Date;
  maxDate?: Date;
  onRangeChange: (start: Date, end: Date) => void;
  theme: string;
  leftInsetPx?: number;
  rightInsetPx?: number;
}

export type { AdvancedDateNavigatorProps };

const AdvancedDateNavigator: React.FC<AdvancedDateNavigatorProps> = ({
  startDate,
  endDate,
  minDate,
  maxDate,
  onRangeChange,
  theme,
  leftInsetPx,
  rightInsetPx,
}) => {
  const isDarkMode = theme === 'dark';
  const fallbackMaxDate = React.useMemo(() => new Date(), []);
  
  // Dynamic boundaries - use UTC to avoid timezone issues
  const minTimestamp = minDate 
    ? Date.UTC(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())
    : Date.UTC(2011, 0, 1);
  
  const maxTimestamp = maxDate 
    ? Date.UTC(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())
    : Date.UTC(fallbackMaxDate.getFullYear(), fallbackMaxDate.getMonth(), fallbackMaxDate.getDate());
  
  // Extract years for labels
  const minYear = minDate ? minDate.getFullYear() : 2011;
  const maxYear = maxDate ? maxDate.getFullYear() : fallbackMaxDate.getFullYear();
  
  const [isDragging, setIsDragging] = useState<'start' | 'end' | 'range' | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleWidthPx = 16;

  const backgroundColor = isDarkMode ? '#1f2937' : '#ffffff';
  const borderColor = isDarkMode ? '#374151' : '#e5e7eb';
  const textColor = isDarkMode ? '#e5e7eb' : '#374151';

  // Convert timestamp to position (0 to 1)
  const timestampToPosition = useCallback((timestamp: number) => {
    // Normalize to start of day using UTC
    const date = new Date(timestamp);
    const normalizedTimestamp = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    
    return (normalizedTimestamp - minTimestamp) / (maxTimestamp - minTimestamp);
  }, [minTimestamp, maxTimestamp]);

  // Get position in pixels - allow separate left/right insets
  // Requested: restore left to previous (0), keep right inset (~10px)
  const leftPaddingPx = leftInsetPx ?? 0;
  const rightPaddingPx = rightInsetPx ?? 10;
  
  const getPixelPosition = useCallback((date: Date) => {
    if (!containerRef.current) return 0;
    const containerWidth = Math.max(1, containerRef.current.clientWidth - leftPaddingPx - rightPaddingPx);
    const position = timestampToPosition(date.getTime());
    
    // Clamp position to ensure end handle reaches the actual end
    const clampedPosition = Math.min(1, Math.max(0, position));
    
    return clampedPosition * containerWidth + leftPaddingPx;
  }, [timestampToPosition, leftPaddingPx, rightPaddingPx]);

  const handleMouseDown = useCallback((e: React.MouseEvent, type: 'start' | 'end' | 'range') => {
    e.preventDefault();
    setIsDragging(type);
    
    if (type === 'range') {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const startPos = getPixelPosition(startDate);
        setDragOffset(e.clientX - rect.left - startPos);
      }
    }
  }, [startDate, getPixelPosition]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    
    // Calculate date from pixel inline to avoid dependency issues
    const containerWidth = Math.max(1, containerRef.current.clientWidth - leftPaddingPx - rightPaddingPx);
    const relativeX = Math.max(0, Math.min(containerWidth, x - leftPaddingPx));
    const position = relativeX / containerWidth;
    const timestampFromX = minTimestamp + position * (maxTimestamp - minTimestamp);
    
    // Round to nearest day using UTC to prevent timezone shifts
    const tempDate = new Date(timestampFromX);
    const newDate = new Date(Date.UTC(tempDate.getUTCFullYear(), tempDate.getUTCMonth(), tempDate.getUTCDate()));
    
    if (isDragging === 'start') {
      const currentEnd = tempEndDate !== null ? tempEndDate : endDate;
      const oneDayBefore = new Date(currentEnd.getTime() - 86400000);
      const clampedTimestamp = Math.max(minTimestamp, Math.min(newDate.getTime(), oneDayBefore.getTime()));
      const clampedTempDate = new Date(clampedTimestamp);
      const clampedDate = new Date(Date.UTC(clampedTempDate.getUTCFullYear(), clampedTempDate.getUTCMonth(), clampedTempDate.getUTCDate()));
      
      // Only update if date actually changed (prevents redundant updates)
      if (!tempStartDate || clampedDate.getTime() !== tempStartDate.getTime()) {
        setTempStartDate(clampedDate);
        onRangeChange(clampedDate, currentEnd);
      }
    } else if (isDragging === 'end') {
      const currentStart = tempStartDate !== null ? tempStartDate : startDate;
      const oneDayAfter = new Date(currentStart.getTime() + 86400000);
      const clampedTimestamp = Math.max(oneDayAfter.getTime(), Math.min(maxTimestamp, newDate.getTime()));
      const clampedTempDate = new Date(clampedTimestamp);
      const clampedDate = new Date(Date.UTC(clampedTempDate.getUTCFullYear(), clampedTempDate.getUTCMonth(), clampedTempDate.getUTCDate()));
      
      // Only update if date actually changed
      if (!tempEndDate || clampedDate.getTime() !== tempEndDate.getTime()) {
        setTempEndDate(clampedDate);
        onRangeChange(currentStart, clampedDate);
      }
    } else if (isDragging === 'range') {
      const currentStart = tempStartDate !== null ? tempStartDate : startDate;
      const currentEnd = tempEndDate !== null ? tempEndDate : endDate;
      const rangeSize = currentEnd.getTime() - currentStart.getTime();
      
      const offsetX = x - dragOffset;
      const relativeOffsetX = Math.max(0, Math.min(containerWidth, offsetX - leftPaddingPx));
      const offsetPosition = relativeOffsetX / containerWidth;
      const newStartTimestamp = minTimestamp + offsetPosition * (maxTimestamp - minTimestamp);
      
      const clampedStartTimestamp = Math.max(minTimestamp, Math.min(maxTimestamp - rangeSize, newStartTimestamp));
      const clampedStartTempDate = new Date(clampedStartTimestamp);
      const clampedStart = new Date(Date.UTC(clampedStartTempDate.getUTCFullYear(), clampedStartTempDate.getUTCMonth(), clampedStartTempDate.getUTCDate()));
      const clampedEndTempDate = new Date(clampedStartTimestamp + rangeSize);
      const clampedEnd = new Date(Date.UTC(clampedEndTempDate.getUTCFullYear(), clampedEndTempDate.getUTCMonth(), clampedEndTempDate.getUTCDate()));
      
      // Only update if dates actually changed
      if (!tempStartDate || clampedStart.getTime() !== tempStartDate.getTime()) {
        setTempStartDate(clampedStart);
        setTempEndDate(clampedEnd);
        onRangeChange(clampedStart, clampedEnd);
      }
    }
  }, [isDragging, startDate, endDate, dragOffset, minTimestamp, maxTimestamp, onRangeChange, tempStartDate, tempEndDate, leftPaddingPx, rightPaddingPx]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
    setDragOffset(0);
    setTempStartDate(null);
    setTempEndDate(null);
    document.body.style.cursor = '';
  }, []);

  // Add global mouse event listeners
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const currentStartDate = tempStartDate !== null ? tempStartDate : startDate;
  const currentEndDate = tempEndDate !== null ? tempEndDate : endDate;
  const startPos = getPixelPosition(currentStartDate);
  const endPos = getPixelPosition(currentEndDate);
  const endHandleLeft = Math.max(
    0,
    Math.min(
      Math.max(0, (containerRef.current?.clientWidth ?? 0) - rightPaddingPx - handleWidthPx),
      endPos - handleWidthPx
    )
  );

  // Generate year labels (every 2 years + always show last year)
  // Skip first year if it's too close to the start handle
  const yearLabels = [];
  for (let year = minYear; year <= maxYear; year += 2) {
    const pos = getPixelPosition(new Date(year, 0, 1));
    // Skip first year if it's within 30px of the start position
    if (year === minYear && Math.abs(pos - startPos) < 30) {
      continue;
    }
    yearLabels.push({ year, pos });
  }
  // Always add the last year if it's not already included
  if (maxYear % 2 !== 0 && !yearLabels.some(label => label.year === maxYear)) {
    const pos = getPixelPosition(new Date(maxYear, 0, 1));
    yearLabels.push({ year: maxYear, pos });
  }

  return (
    <div
      id="advanced-date-navigator"
      style={{
        backgroundColor,
        borderRadius: '0.5rem',
        padding: '0rem',
        border: `0px solid ${borderColor}`,
        marginTop: '-40px',
      }}
    >
      {/* Navigator */}
      <div
        ref={containerRef}
        style={{
          height: '2.5rem',
          position: 'relative',
          backgroundColor: 'transparent',
          borderRadius: '0.375rem',
          cursor: 'pointer',
          border: 'none',
          overflow: 'visible',
        }}
      >
        {/* Fixed gray background frame - always visible from minYear to maxYear */}
        <div
          style={{
            position: 'absolute',
            left: `${leftPaddingPx}px`,
            right: `${rightPaddingPx}px`,
            height: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'transparent',
            border: '2px solid #D9D9D9',
            borderRadius: '0.375rem',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />

        {/* Start handle - circular, positioned inside the frame */}
        <div
          style={{
            position: 'absolute',
            left: `${startPos - 6}px`,
            top: '50%',
            transform: 'translateY(-50%)',
            width: `${handleWidthPx + 12}px`,
            height: '1.5rem',
            backgroundColor: 'transparent',
            borderRadius: '0.5rem',
            cursor: 'ew-resize',
            zIndex: 15,
          }}
          onMouseDown={(e) => handleMouseDown(e, 'start')}
          onMouseEnter={() => { document.body.style.cursor = 'ew-resize'; }}
          onMouseLeave={() => { document.body.style.cursor = ''; }}
        >
          <div
            style={{
              position: 'absolute',
              left: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: `${handleWidthPx}px`,
              height: '1rem',
              backgroundColor: 'white',
              borderRadius: '50%',
              border: '2px solid black',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              pointerEvents: 'none',
            }}
          />
        </div>

        {/* End handle - circular, positioned inside the frame */}
        <div
          style={{
            position: 'absolute',
            left: `${endHandleLeft - 6}px`,
            top: '50%',
            transform: 'translateY(-50%)',
            width: `${handleWidthPx + 12}px`,
            height: '1.5rem',
            backgroundColor: 'transparent',
            borderRadius: '0.5rem',
            cursor: 'ew-resize',
            zIndex: 15,
          }}
          onMouseDown={(e) => handleMouseDown(e, 'end')}
          onMouseEnter={() => { document.body.style.cursor = 'ew-resize'; }}
          onMouseLeave={() => { document.body.style.cursor = ''; }}
        >
          <div
            style={{
              position: 'absolute',
              left: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: `${handleWidthPx}px`,
              height: '1rem',
              backgroundColor: 'white',
              borderRadius: '50%',
              border: '2px solid black',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              pointerEvents: 'none',
            }}
          />
        </div>

        {/* Range selection area - 16px height with rounded corners, behind the handles */}
        <div
          style={{
            position: 'absolute',
            left: `${startPos}px`,
            width: `${endPos - startPos}px`,
            height: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: '#F1F7FF',
            border: '2px solid #D9D9D9',
            borderRadius: '0.375rem',
            cursor: 'move',
            zIndex: 5,
          }}
          onMouseDown={(e) => handleMouseDown(e, 'range')}
        />
      </div>

      {/* Year labels */}
      <div
        id="date-navigator-year-labels"
        style={{
          position: 'relative',
          marginTop: '-29px',
          height: '0.625rem',
          zIndex: 10,
          pointerEvents: 'none',
        }}
      >
        {yearLabels.map(({ year, pos }) => (
          <span
            key={year}
            style={{
              position: 'absolute',
              left: `${pos}px`,
              transform: 'translateX(-50%)',
              fontSize: '0.75rem',
              color: textColor,
              pointerEvents: 'none',
            }}
          >
            {year}
          </span>
        ))}
      </div>


    </div>
  );
};

export default AdvancedDateNavigator;





